/**
 * ConsumptionCertificate codec — the pure decode/encode pair + the field-granular
 * cross-derived setters (MODULE-CONTRACT §2, WAVE2-DECISIONS D-W2-11 C1-C6).
 *
 * The persisted DTO is a LOSSY, NORMALIZED projection of a rich working state
 * (moment dates, keyed heater/billing collections, a Set of energetic measures,
 * mid-edit strings). This file is the single home of all derivation between the
 * two representations, so the fixed-point property `encode(decode(dto)) === dto`
 * (JSON-normalized) is trivial to test and the cross-derived logic lives in ONE
 * place.
 *
 * ⚠️ THE SHIPPING WRITE PATH IS THE FIELD-GRANULAR SETTERS (C1), NOT whole-DTO
 * `setConfig(encode(vm))`. Whole-DTO re-encode corrupts on any decode/encode
 * asymmetry the fixture doesn't exercise. `encodeConsumption` is kept ONLY as
 * (a) a test oracle for the fixed-point property and (b) the shared source of the
 * cross-derived sibling derivations (`deriveBuildingPeriod`,
 * `deriveEnergeticRenovations`) that each field-granular setter applies LOCALLY.
 * Every cross-derived setter recomputes from the LIVE persisted DTO (`getState()`
 * via the ops hook), never a mount-time decode snapshot.
 *
 * The highest-risk path is the `energeticRenovations` re-derivation cluster
 * (`getValidEnergeticConditionType` port) — it rewrites three sibling wire booleans
 * from `residentialUnit` / `buildingYear` / `isConstructionConfirmed` /
 * `currentEnergeticConditions`, so any divergence silently corrupts a resumed
 * draft on first edit. Ported byte-for-byte from `getConsumptionCertificateRequestData`.
 *
 * ZERO React / store imports — pure functions only.
 */

import moment, { type Moment } from 'moment-timezone'

import { toNumber } from 'utils/formatting/numberUtils'

import {
  ConsumptionCertificateLivingUnits,
  ConsumptionCertificateBuildingPeriod,
  ConsumptionCertificateBasementType,
  ConsumptionCertificateVentilationType,
  RenewableEnergySource,
  RenewableEnergySourceUsage,
  FuelType,
  HotWaterGenerationType,
  HeaterConsumptionUnit,
  EnergeticMeasure,
  type BuildingLocation
} from 'constants/purchaseFlow/consumptionCertificateTypes'
import { BuildingType, EnergyPassReason } from 'constants/purchaseFlow/sharedEnergyCertificateTypes'

import type {
  ConsumptionCertificateConfigDTO,
  ConsumptionCertificateHeaterPayloadDTO,
  EnergeticRenovationsDTO,
  HeaterBillingPeriodPayloadDTO
} from './consumptionCertificate.dto'

const DATE_FORMAT = 'DD.MM.YYYY'

// ─── View-model shapes (rich, editable; localId dropped by encode — C6) ───────

/** A billing-period row's editable working state. `localId` is a STABLE per-row id
 *  for React keys + leaf-local mid-edit — NOT persisted, dropped by encode (C6). */
export interface BillingPeriodVM {
  localId: string
  startDate: Moment | null
  endDate: Moment | null
  /** Mid-edit string, may carry a decimal comma. Encodes via `toNumber()`. */
  consumption: string
  hotWaterConsumptionKwh: number | null
}

/** A heater card's editable working state. `localId` stable per-row (C6). */
export interface HeaterVM {
  localId: string
  fuelType?: FuelType
  hotWaterType?: HotWaterGenerationType
  unitCaptureType?: HeaterConsumptionUnit
  yearOfManufacture: Moment | null
  /** Mid-edit string, may carry a decimal comma; encoded only when fuel is district heating. */
  primaryEnergyFactor: string
  billingPeriodItems: BillingPeriodVM[]
}

/** The full decoded view model. Rich structures the DTO cannot hold directly. */
export interface ConsumptionViewModel {
  buildingType?: BuildingType
  buildingLocation?: BuildingLocation
  residentialUnit: number
  commercialArea: string
  commercialAreaType: string
  constructionYear: string
  yearOfRenovation: string
  livingSpace: string
  isConstructionConfirmed: boolean
  basementType: ConsumptionCertificateBasementType
  ventilationType: ConsumptionCertificateVentilationType
  systemType: RenewableEnergySource
  useType: RenewableEnergySourceUsage
  energeticConditionType: EnergeticMeasure[]
  hasPropertyVacant: boolean
  vacancyArea: string
  vacancyStart: Moment | null
  vacancyEnd: Moment | null
  heaters: HeaterVM[]
  reasonForEnergyPass: EnergyPassReason
  messageToExpert: string
  exteriorViewFilenames: string[]
  heaterNameplateFilenames: string[]
}

// ─── Stable local-id factory (C6) ────────────────────────────────────────────

let _idCounter = 0
/** Monotonic, collision-free local id for CRUD rows. NOT persisted. */
export const makeLocalId = (prefix = 'row'): string => `${prefix}-${_idCounter++}`

// ─── Small pure helpers ───────────────────────────────────────────────────────

const momentOrNull = (value: string | undefined | null, format: string): Moment | null => {
  if (!value) return null
  const m = moment(value, format)
  return m.isValid() ? m : null
}

// ─── Cross-derived sibling derivations (shared by encode + field setters) ─────

/** Living-unit description bucket (legacy: `residentialUnit > 4 ? FIVE_OR_MORE : LESS_THAN_FIVE`). */
export const deriveLivingUnitsDescr = (livingUnits: number): ConsumptionCertificateLivingUnits =>
  livingUnits > 4 ? ConsumptionCertificateLivingUnits.FIVE_OR_MORE : ConsumptionCertificateLivingUnits.LESS_THAN_FIVE

/**
 * Wire `buildingPeriod`: the year-derived period, but OMITTED (`undefined`) when
 * there are ≥5 living units (legacy:
 * `buildingPeriod: livingUnits === FIVE_OR_MORE ? undefined : buildingPeriod`).
 */
export const deriveBuildingPeriod = (
  buildingYear: number,
  livingUnits: number
): ConsumptionCertificateBuildingPeriod | undefined => {
  if (livingUnits > 4) return undefined
  return buildingYear < 1978
    ? ConsumptionCertificateBuildingPeriod.BEFORE_1978
    : ConsumptionCertificateBuildingPeriod.FROM_1978_ON
}

/**
 * The `energeticRenovations` re-derivation cluster (THE highest-risk path). Exact
 * port of `getConsumptionCertificateRequestData`'s `getValidEnergeticConditionType`:
 *  - forces all three false when `<5 units && FROM_1978_ON` (post-1978 small buildings
 *    can't claim renovations);
 *  - forces all three true when the pre-1978 construction note was confirmed;
 *  - otherwise mirrors the selected energetic-condition Set membership.
 *
 * `isShowConstructionNote` is reconstructed from the settled DTO values (legacy used
 * a 500ms-debounced copy purely for UX; the derivation is equivalent at rest).
 */
export const deriveEnergeticRenovations = (params: {
  buildingYear: number
  livingUnits: number
  isConstructionConfirmed: boolean
  energeticConditions: EnergeticMeasure[]
}): EnergeticRenovationsDTO => {
  const { buildingYear, livingUnits, isConstructionConfirmed, energeticConditions } = params

  const yearStr = buildingYear ? String(buildingYear) : ''
  const isShowConstructionNote = livingUnits < 5 && buildingYear < 1978 && yearStr.length >= 4
  const isBefore1978AndConstructionConfirmed = isShowConstructionNote && isConstructionConfirmed

  // The year-derived period (NOT the omitted wire value) — legacy computes the gate
  // against the pre-omit period; for <5 units it is never omitted anyway.
  const period = buildingYear < 1978
    ? ConsumptionCertificateBuildingPeriod.BEFORE_1978
    : ConsumptionCertificateBuildingPeriod.FROM_1978_ON

  const selected = new Set(energeticConditions)
  const getValid = (type: EnergeticMeasure): boolean => {
    if (livingUnits < 5 && period === ConsumptionCertificateBuildingPeriod.FROM_1978_ON) return false
    return isBefore1978AndConstructionConfirmed || selected.has(type)
  }

  return {
    hasDoubleGlazedWindows: getValid(EnergeticMeasure.WINDOW_REPLACEMENT_25_YEARS),
    hasRoofInsulation: getValid(EnergeticMeasure.ROOF_INSULATION),
    hasExteriorWallInsulation: getValid(EnergeticMeasure.EXTERNAL_WALL_INSULATION),
  }
}

// ─── Heater decode / encode ────────────────────────────────────────────────────

const DEFAULT_BILLING_PERIOD_COUNT = 3

const makeDefaultBillingPeriodVM = (): BillingPeriodVM => ({
  localId: makeLocalId('billing'),
  startDate: null,
  endDate: null,
  consumption: '',
  hotWaterConsumptionKwh: null,
})

/** A fresh default heater card (legacy seeded ONE with three billing periods on
 *  mount). Native derives it on read when the DTO has no heaters — never a mount
 *  write; persisted only once the user edits it. */
export const makeDefaultHeaterVM = (): HeaterVM => ({
  localId: makeLocalId('heater'),
  fuelType: undefined,
  hotWaterType: undefined,
  unitCaptureType: undefined,
  yearOfManufacture: null,
  primaryEnergyFactor: '',
  billingPeriodItems: Array.from({ length: DEFAULT_BILLING_PERIOD_COUNT }, makeDefaultBillingPeriodVM),
})

const decodeBillingPeriod = (b: HeaterBillingPeriodPayloadDTO): BillingPeriodVM => ({
  localId: makeLocalId('billing'),
  startDate: momentOrNull(b.startBillingPeriod, DATE_FORMAT),
  endDate: momentOrNull(b.endOfBillingPeriod, DATE_FORMAT),
  consumption: b.consumption != null ? String(b.consumption) : '',
  hotWaterConsumptionKwh: b.hotWaterConsumptionKwh ?? null,
})

const decodeHeater = (h: ConsumptionCertificateHeaterPayloadDTO): HeaterVM => ({
  localId: makeLocalId('heater'),
  fuelType: h.fuel,
  hotWaterType: h.hotWater,
  unitCaptureType: h.unit,
  yearOfManufacture: h.yearOfConstruction ? momentOrNull(String(h.yearOfConstruction), 'YYYY') : null,
  primaryEnergyFactor: h.primaryEnergyFactor != null ? String(h.primaryEnergyFactor) : '',
  billingPeriodItems: (h.billingPeriod ?? []).map(decodeBillingPeriod),
})

/** Decode the DTO heater array. Empty/absent → ONE default heater card (collection
 *  default on read; NOT persisted until the user edits). */
export const decodeHeaters = (heaters: ConsumptionCertificateHeaterPayloadDTO[] | undefined): HeaterVM[] => {
  if (!heaters || heaters.length === 0) return [makeDefaultHeaterVM()]
  return heaters.map(decodeHeater)
}

const encodeBillingPeriod = (
  b: BillingPeriodVM,
  hotWaterType: HotWaterGenerationType | undefined
): HeaterBillingPeriodPayloadDTO => {
  const hotWaterConsumptionKwhValue =
    hotWaterType === HotWaterGenerationType.KNOW_HOT_WATER_CONSUMPTION_IN_KWH ? b.hotWaterConsumptionKwh : 0
  return {
    startBillingPeriod: b.startDate?.format(DATE_FORMAT) ?? '',
    endOfBillingPeriod: b.endDate?.format(DATE_FORMAT) ?? '',
    hotWaterConsumptionKwh: hotWaterConsumptionKwhValue ?? 0,
    consumption: toNumber(b.consumption),
  }
}

/** Encode ONE heater card → wire heater. Faithful port of the serialize map. */
export const encodeHeater = (vm: HeaterVM): ConsumptionCertificateHeaterPayloadDTO => ({
  fuel: vm.fuelType as FuelType,
  unit: vm.unitCaptureType as HeaterConsumptionUnit,
  hotWater: vm.hotWaterType as HotWaterGenerationType,
  yearOfConstruction: parseInt(vm.yearOfManufacture?.format('YYYY') ?? '0'),
  primaryEnergyFactor: vm.fuelType === FuelType.DISTRICT_HEATING ? toNumber(vm.primaryEnergyFactor) : 0,
  billingPeriod: vm.billingPeriodItems.map((b) => encodeBillingPeriod(b, vm.hotWaterType)),
})

/** Encode the whole heater VM array → wire array (the `heaters`-scoped field patch). */
export const encodeHeaters = (heaters: HeaterVM[]): ConsumptionCertificateHeaterPayloadDTO[] =>
  heaters.map(encodeHeater)

// ─── Vacancy encode (legacy derives the period array at serialize time) ────────

/**
 * Wire vacancy: a single period ONLY when vacant AND an area was entered (legacy:
 * `(hasPropertyVacant && area) ? [{...}] : []`). Blank area → empty periods, so the
 * in-progress dates are intentionally not persisted (legacy parity; the panel holds
 * them in leaf-local state while editing).
 */
export const encodeVacancy = (params: {
  present: boolean
  area: string
  start: Moment | null
  end: Moment | null
}): ConsumptionCertificateConfigDTO['vacancy'] => {
  const { present, area, start, end } = params
  return {
    vacancyPresent: present,
    vacancyPeriods:
      present && area
        ? [
            {
              areaVacancyInSquareMeters: toNumber(area),
              startVacancy: start?.format(DATE_FORMAT) ?? '',
              endVacancy: end?.format(DATE_FORMAT) ?? '',
            },
          ]
        : [],
  }
}

// ─── Full decode ────────────────────────────────────────────────────────────────

export const decodeConsumption = (dto: ConsumptionCertificateConfigDTO): ConsumptionViewModel => {
  const period0 = dto.vacancy?.vacancyPeriods?.[0]
  return {
    buildingType: dto.buildingType || undefined,
    buildingLocation: dto.buildingLocation || undefined,
    residentialUnit: dto.livingUnits ?? 1,
    commercialArea: dto.commercialSpaceInSquareMeters ? String(dto.commercialSpaceInSquareMeters) : '',
    commercialAreaType: dto.typeOfUseCommercial ?? '',
    constructionYear: dto.buildingYear ? String(dto.buildingYear) : '',
    yearOfRenovation: dto.renovationYear ? String(dto.renovationYear) : '',
    livingSpace: dto.totalLivingArea ? String(dto.totalLivingArea) : '',
    isConstructionConfirmed: !!dto.isConstructionConfirmed,
    basementType: dto.basement ?? ConsumptionCertificateBasementType.NO_BASEMENT,
    ventilationType: dto.ventilation ?? ConsumptionCertificateVentilationType.STANDARD_WINDOW_VENTILATION,
    systemType: dto.renewableEnergies ?? RenewableEnergySource.NONE,
    useType: dto.renewableEnergiesUsage ?? RenewableEnergySourceUsage.NONE,
    energeticConditionType: [...(dto.currentEnergeticConditions ?? [])],
    hasPropertyVacant: dto.vacancy?.vacancyPresent ?? true,
    vacancyArea: period0?.areaVacancyInSquareMeters ? String(period0.areaVacancyInSquareMeters) : '',
    vacancyStart: momentOrNull(period0?.startVacancy, DATE_FORMAT),
    vacancyEnd: momentOrNull(period0?.endVacancy, DATE_FORMAT),
    heaters: decodeHeaters(dto.heaters),
    reasonForEnergyPass: dto.reasonForEnergyPass ?? EnergyPassReason.NEW_RENTAL,
    messageToExpert: dto.messageToExpert ?? '',
    exteriorViewFilenames: [...(dto.exteriorViewFilenames ?? [])],
    heaterNameplateFilenames: [...(dto.heaterNameplateFilenames ?? [])],
  }
}

// ─── Full encode (TEST ORACLE ONLY — not the shipping write path, C1) ─────────

/**
 * Faithful port of `getConsumptionCertificateRequestData` (+ the three additive
 * fields carried through). Returns `undefined` when the required property fields
 * are missing, exactly like legacy. Used ONLY as the fixed-point test oracle and
 * to validate the field-granular setters against a single source of truth — the
 * shipping write path is the field-granular setters (C1).
 */
export const encodeConsumption = (vm: ConsumptionViewModel): ConsumptionCertificateConfigDTO | undefined => {
  if (!vm.buildingType || !vm.constructionYear || !vm.livingSpace || !vm.buildingLocation) return undefined

  const livingUnitsDescr = deriveLivingUnitsDescr(vm.residentialUnit)
  const buildingYear = parseInt(vm.constructionYear)
  const buildingPeriod = deriveBuildingPeriod(buildingYear, vm.residentialUnit)
  const energeticRenovations = deriveEnergeticRenovations({
    buildingYear,
    livingUnits: vm.residentialUnit,
    isConstructionConfirmed: vm.isConstructionConfirmed,
    energeticConditions: vm.energeticConditionType,
  })

  return {
    livingUnitsDescr,
    buildingPeriod,
    energeticRenovations,
    currentEnergeticConditions: [...vm.energeticConditionType],
    buildingType: vm.buildingType,
    typeOfUseCommercial: vm.buildingType === BuildingType.MIXED_USE_BUILDING ? vm.commercialAreaType : '',
    commercialSpaceInSquareMeters:
      vm.buildingType === BuildingType.MIXED_USE_BUILDING ? parseInt(vm.commercialArea) : 0,
    buildingLocation: vm.buildingLocation,
    buildingYear,
    renovationYear: parseInt(vm.yearOfRenovation),
    livingUnits: vm.residentialUnit,
    totalLivingArea: toNumber(vm.livingSpace),
    renewableEnergies: vm.systemType,
    renewableEnergiesUsage: vm.useType,
    ventilation: vm.ventilationType,
    basement: vm.basementType,
    heaters: encodeHeaters(vm.heaters),
    vacancy: encodeVacancy({
      present: vm.hasPropertyVacant,
      area: vm.vacancyArea,
      start: vm.vacancyStart,
      end: vm.vacancyEnd,
    }),
    reasonForEnergyPass: vm.reasonForEnergyPass,
    messageToExpert: vm.messageToExpert,
    // additive FE-only fields carried through so the fixed-point property holds
    isConstructionConfirmed: vm.isConstructionConfirmed,
    exteriorViewFilenames: [...vm.exteriorViewFilenames],
    heaterNameplateFilenames: [...vm.heaterNameplateFilenames],
  }
}

// ─── Field-granular cross-derived setters (THE shipping write path — C1) ──────
//
// Each returns ONLY {owned field + declared cross-derived siblings}, computed from
// the passed-in CURRENT (live) DTO. The single-field-edit isolation test (C2b)
// asserts no field outside each declared set changes. These are the ONLY functions
// the UI uses to mutate cross-derived fields.

/** residentialUnit → livingUnits + livingUnitsDescr + buildingPeriod + energeticRenovations. */
export const patchResidentialUnit = (
  current: ConsumptionCertificateConfigDTO,
  livingUnits: number
): Partial<ConsumptionCertificateConfigDTO> => ({
  livingUnits,
  livingUnitsDescr: deriveLivingUnitsDescr(livingUnits),
  buildingPeriod: deriveBuildingPeriod(current.buildingYear ?? 0, livingUnits),
  energeticRenovations: deriveEnergeticRenovations({
    buildingYear: current.buildingYear ?? 0,
    livingUnits,
    isConstructionConfirmed: !!current.isConstructionConfirmed,
    energeticConditions: current.currentEnergeticConditions ?? [],
  }),
})

/** constructionYear → buildingYear + buildingPeriod + energeticRenovations. */
export const patchConstructionYear = (
  current: ConsumptionCertificateConfigDTO,
  yearStr: string
): Partial<ConsumptionCertificateConfigDTO> => {
  const buildingYear = parseInt(yearStr) // NaN when blank (legacy quirk, reproduced)
  const livingUnits = current.livingUnits ?? 1
  return {
    buildingYear,
    buildingPeriod: deriveBuildingPeriod(buildingYear, livingUnits),
    energeticRenovations: deriveEnergeticRenovations({
      buildingYear,
      livingUnits,
      isConstructionConfirmed: !!current.isConstructionConfirmed,
      energeticConditions: current.currentEnergeticConditions ?? [],
    }),
  }
}

/** energetic-Set toggle → currentEnergeticConditions + energeticRenovations. */
export const patchEnergeticConditions = (
  current: ConsumptionCertificateConfigDTO,
  conditions: EnergeticMeasure[]
): Partial<ConsumptionCertificateConfigDTO> => ({
  currentEnergeticConditions: [...conditions],
  energeticRenovations: deriveEnergeticRenovations({
    buildingYear: current.buildingYear ?? 0,
    livingUnits: current.livingUnits ?? 1,
    isConstructionConfirmed: !!current.isConstructionConfirmed,
    energeticConditions: conditions,
  }),
})

/** isConstructionConfirmed → the additive field + energeticRenovations. */
export const patchIsConstructionConfirmed = (
  current: ConsumptionCertificateConfigDTO,
  confirmed: boolean
): Partial<ConsumptionCertificateConfigDTO> => ({
  isConstructionConfirmed: confirmed,
  energeticRenovations: deriveEnergeticRenovations({
    buildingYear: current.buildingYear ?? 0,
    livingUnits: current.livingUnits ?? 1,
    isConstructionConfirmed: confirmed,
    energeticConditions: current.currentEnergeticConditions ?? [],
  }),
})

/** buildingType → buildingType (+ blank commercial when leaving MIXED_USE_BUILDING). */
export const patchBuildingType = (
  _current: ConsumptionCertificateConfigDTO,
  buildingType: BuildingType
): Partial<ConsumptionCertificateConfigDTO> => {
  if (buildingType === BuildingType.MIXED_USE_BUILDING) return { buildingType }
  return { buildingType, typeOfUseCommercial: '', commercialSpaceInSquareMeters: 0 }
}
